/**
 * HR Engine - Composant Dialog de création/édition de Fiche de Poste
 * Réutilisable dans JobProfilesPage et EmployeeFormPage
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Briefcase,
  Plus,
  Trash2,
  Target,
  Award,
  ChevronRight,
  ChevronDown,
  Loader2,
  Check,
  Download,
  FileText,
  Users,
  AlertTriangle
} from 'lucide-react';

// Skill levels configuration
const SKILL_LEVELS = {
  1: { label: 'Débutant', color: 'bg-slate-100 text-slate-700' },
  2: { label: 'Intermédiaire', color: 'bg-blue-100 text-blue-700' },
  3: { label: 'Confirmé', color: 'bg-green-100 text-green-700' },
  4: { label: 'Avancé', color: 'bg-purple-100 text-purple-700' },
  5: { label: 'Expert', color: 'bg-amber-100 text-amber-700' }
};

export default function JobProfileDialog({
  open,
  onOpenChange,
  mode = 'create', // 'create' or 'edit'
  profileData = null,
  companyId,
  departments = [],
  onSaveSuccess, // Callback after successful save, receives the saved profile
  prefilledName = '', // Pre-filled name for quick creation from employee form
  prefilledDepartmentId = '' // Pre-filled department
}) {
  const navigate = useNavigate();
  
  // Form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    departmentId: '',
    description: '',
    skills: [],
    objectives: [],
    missions: '',
    responsibilities: '',
    requirements: '',
    workingConditions: '',
    hierarchicalPosition: '',
    // Phase 1 — Référentiel carrière
    familyId: '',
    seniorityLevelId: '',
    minYearsExperience: '',
    mandatoryTrainings: [],
    recommendedCertifications: []
  });

  const [selectedSkills, setSelectedSkills] = useState({});
  const [activeFormTab, setActiveFormTab] = useState('general');
  const [saving, setSaving] = useState(false);

  // Data for skills
  const [allSkills, setAllSkills] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);

  // Phase 1 — Career referential lookups
  const [families, setFamilies] = useState([]);
  const [seniorityLevels, setSeniorityLevels] = useState([]);
  const [trainingInput, setTrainingInput] = useState('');
  const [certInput, setCertInput] = useState('');

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      loadSkillsData();
      loadCareerReferential();
      if (mode === 'edit' && profileData) {
        setProfileForm({
          name: profileData.name || '',
          departmentId: profileData.departmentId || '',
          description: profileData.description || '',
          skills: profileData.skills || [],
          objectives: profileData.objectives || [],
          missions: profileData.missions || '',
          responsibilities: profileData.responsibilities || '',
          requirements: profileData.requirements || '',
          workingConditions: profileData.workingConditions || '',
          hierarchicalPosition: profileData.hierarchicalPosition || '',
          familyId: profileData.familyId || '',
          seniorityLevelId: profileData.seniorityLevelId || '',
          minYearsExperience: profileData.minYearsExperience ?? '',
          mandatoryTrainings: profileData.mandatoryTrainings || [],
          recommendedCertifications: profileData.recommendedCertifications || []
        });
        // Initialize selected skills
        const selected = {};
        (profileData.skills || []).forEach(s => {
          selected[s.skillId] = {
            selected: true,
            requiredLevel: s.requiredLevel || 3,
            importance: s.importance || 'medium'
          };
        });
        setSelectedSkills(selected);
      } else {
        // Reset form for new profile
        setProfileForm({
          name: prefilledName || '',
          departmentId: prefilledDepartmentId || '',
          description: '',
          skills: [],
          objectives: [],
          missions: '',
          responsibilities: '',
          requirements: '',
          workingConditions: '',
          hierarchicalPosition: '',
          familyId: '',
          seniorityLevelId: '',
          minYearsExperience: '',
          mandatoryTrainings: [],
          recommendedCertifications: []
        });
        setSelectedSkills({});
      }
      setActiveFormTab('general');
      setTrainingInput('');
      setCertInput('');
    }
  }, [open, mode, profileData, prefilledName, prefilledDepartmentId]);

  const loadCareerReferential = async () => {
    try {
      const [fRes, lRes] = await Promise.all([
        api.get('/career/job-families').catch(() => ({ data: { items: [] } })),
        api.get('/career/seniority-levels').catch(() => ({ data: { items: [] } })),
      ]);
      setFamilies(fRes.data?.items || []);
      setSeniorityLevels(lRes.data?.items || []);
    } catch {
      // best-effort
    }
  };

  const loadSkillsData = async () => {
    if (!companyId) return;
    
    try {
      setLoadingSkills(true);
      const [categoriesRes, skillsRes] = await Promise.all([
        api.get(`/skills/categories?company_id=${companyId}`),
        api.get(`/skills/library?company_id=${companyId}`)
      ]);
      
      setCategories(categoriesRes.data.categories || []);
      setAllSkills(skillsRes.data.skills || []);
    } catch (error) {
      console.error('Error loading skills:', error);
    } finally {
      setLoadingSkills(false);
    }
  };

  // Add a new objective
  const addObjective = () => {
    const newObjective = {
      id: `obj_${Date.now()}`,
      title: '',
      description: '',
      specific: '',
      measurable: '',
      achievable: '',
      relevant: '',
      timeBound: '',
      weight: 20
    };
    setProfileForm(prev => ({
      ...prev,
      objectives: [...prev.objectives, newObjective]
    }));
  };

  // Update an objective
  const updateObjective = (index, field, value) => {
    setProfileForm(prev => {
      const newObjectives = [...prev.objectives];
      newObjectives[index] = { ...newObjectives[index], [field]: value };
      return { ...prev, objectives: newObjectives };
    });
  };

  // Remove an objective
  const removeObjective = (index) => {
    setProfileForm(prev => ({
      ...prev,
      objectives: prev.objectives.filter((_, i) => i !== index)
    }));
  };

  // Toggle skill selection
  const toggleSkillSelection = (skillId) => {
    setSelectedSkills(prev => {
      if (prev[skillId]?.selected) {
        const { [skillId]: removed, ...rest } = prev;
        return rest;
      } else {
        return {
          ...prev,
          [skillId]: {
            selected: true,
            requiredLevel: 3,
            importance: 'medium'
          }
        };
      }
    });
  };

  // Update skill requirement
  const updateSkillRequirement = (skillId, field, value) => {
    setSelectedSkills(prev => ({
      ...prev,
      [skillId]: {
        ...prev[skillId],
        [field]: value
      }
    }));
  };

  // Import default skills
  const handleImportDefaults = async () => {
    try {
      await api.post(`/skills/seed-defaults?company_id=${companyId}`);
      toast.success('Compétences par défaut importées !');
      loadSkillsData();
    } catch (error) {
      toast.error('Erreur lors de l\'import');
    }
  };

  // Group skills by subcategory
  const groupedSkills = categories.flatMap(cat => 
    (cat.subcategories || []).map(sub => ({
      id: sub.id,
      name: sub.name,
      color: cat.color,
      skills: allSkills.filter(s => s.subcategoryId === sub.id)
    }))
  ).filter(g => g.skills.length > 0);

  // Save profile
  const handleSaveProfile = async () => {
    if (!profileForm.name.trim()) {
      toast.error('Le nom de la fiche est requis');
      setActiveFormTab('general');
      return;
    }
    
    // Build skills array from selected
    const skills = Object.entries(selectedSkills)
      .filter(([_, data]) => data.selected)
      .map(([skillId, data]) => ({
        skillId,
        requiredLevel: data.requiredLevel,
        importance: data.importance
      }));
    
    // Validate objectives weights if there are objectives
    const totalWeight = profileForm.objectives.reduce((sum, obj) => sum + (obj.weight || 0), 0);
    if (profileForm.objectives.length > 0 && totalWeight !== 100) {
      toast.error(`La somme des pondérations des objectifs doit être 100% (actuellement ${totalWeight}%)`);
      setActiveFormTab('objectives');
      return;
    }
    
    const payload = {
      name: profileForm.name,
      title: profileForm.name, // Also save as title for compatibility
      departmentId: profileForm.departmentId === 'none' ? null : profileForm.departmentId,
      description: profileForm.description,
      skills,
      objectives: profileForm.objectives.filter(obj => obj.title.trim()),
      missions: profileForm.missions,
      responsibilities: profileForm.responsibilities,
      requirements: profileForm.requirements,
      workingConditions: profileForm.workingConditions,
      hierarchicalPosition: profileForm.hierarchicalPosition,
      // Phase 1 — Référentiel carrière
      familyId: profileForm.familyId || null,
      seniorityLevelId: profileForm.seniorityLevelId || null,
      minYearsExperience:
        profileForm.minYearsExperience === '' || profileForm.minYearsExperience === null
          ? null
          : Number(profileForm.minYearsExperience),
      mandatoryTrainings: profileForm.mandatoryTrainings || [],
      recommendedCertifications: profileForm.recommendedCertifications || []
    };
    
    try {
      setSaving(true);
      let savedProfile;
      
      if (mode === 'create') {
        const response = await api.post(`/skills/job-profiles?company_id=${companyId}`, payload);
        savedProfile = response.data;
        toast.success('Fiche de poste créée avec succès !');
      } else {
        const response = await api.put(`/skills/job-profiles/${profileData.id}?company_id=${companyId}`, payload);
        savedProfile = response.data;
        toast.success('Fiche mise à jour');
      }
      
      onOpenChange(false);
      
      if (onSaveSuccess) {
        onSaveSuccess(savedProfile);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const selectedSkillsCount = Object.values(selectedSkills).filter(s => s.selected).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-purple-600" />
            {mode === 'create' ? 'Nouvelle fiche de poste' : 'Modifier la fiche de poste'}
          </DialogTitle>
          <DialogDescription>
            Définissez les informations, compétences et objectifs requis pour ce poste.
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeFormTab} onValueChange={setActiveFormTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="general">Général</TabsTrigger>
            <TabsTrigger value="objectives">
              Objectifs
              {profileForm.objectives.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">{profileForm.objectives.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="skills">
              Compétences
              {selectedSkillsCount > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">{selectedSkillsCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="details">Détails</TabsTrigger>
            <TabsTrigger value="career" data-testid="tab-career">Carrière</TabsTrigger>
          </TabsList>
          
          {/* TAB: Général */}
          <TabsContent value="general" className="space-y-4 mt-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Nom de la fiche *</Label>
                <Input
                  id="name"
                  placeholder="Ex: Développeur Full Stack"
                  value={profileForm.name}
                  onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Département</Label>
                <Select 
                  value={profileForm.departmentId || "none"}
                  onValueChange={(value) => setProfileForm({...profileForm, departmentId: value === "none" ? "" : value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Aucun" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description du poste</Label>
              <Textarea
                placeholder="Décrivez brièvement le poste et ses responsabilités..."
                value={profileForm.description}
                onChange={(e) => setProfileForm({...profileForm, description: e.target.value})}
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Position hiérarchique</Label>
              <Input
                placeholder="Ex: N-1: Directeur Technique, N+1: Développeur Junior"
                value={profileForm.hierarchicalPosition}
                onChange={(e) => setProfileForm({...profileForm, hierarchicalPosition: e.target.value})}
              />
            </div>
          </TabsContent>
          
          {/* TAB: Objectifs SMART */}
          <TabsContent value="objectives" className="space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Target className="w-5 h-5 text-amber-600" />
                  Objectifs SMART
                </Label>
                <p className="text-sm text-slate-500">
                  Définissez les objectifs de performance pour ce poste. La pondération totale doit être égale à 100%.
                </p>
              </div>
            </div>
            
            {/* Quick Add Section */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Plus className="w-5 h-5 text-amber-600" />
                  <span className="font-medium text-amber-900">Ajouter un nouvel objectif</span>
                </div>
                <Button 
                  onClick={addObjective} 
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Ajouter
                </Button>
              </div>
            </div>
            
            {profileForm.objectives.length === 0 ? (
              <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                <Target className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">Aucun objectif défini</h3>
                <p className="text-slate-500 mb-4 max-w-md mx-auto">
                  Les objectifs SMART permettent d'évaluer la performance des employés de manière structurée et mesurable.
                </p>
                <Button onClick={addObjective} className="bg-amber-600 hover:bg-amber-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Créer le premier objectif
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Weight Summary Bar */}
                <div className={`p-3 rounded-lg flex items-center justify-between ${
                  profileForm.objectives.reduce((s, o) => s + (o.weight || 0), 0) === 100 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-amber-50 border border-amber-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {profileForm.objectives.reduce((s, o) => s + (o.weight || 0), 0) === 100 ? (
                      <Check className="w-5 h-5 text-green-600" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    )}
                    <span className={`font-medium ${
                      profileForm.objectives.reduce((s, o) => s + (o.weight || 0), 0) === 100 
                        ? 'text-green-700' 
                        : 'text-amber-700'
                    }`}>
                      Pondération totale : {profileForm.objectives.reduce((s, o) => s + (o.weight || 0), 0)}% / 100%
                    </span>
                  </div>
                  <span className="text-sm text-slate-500">
                    {profileForm.objectives.length} objectif(s)
                  </span>
                </div>

                {/* Objectives List */}
                {profileForm.objectives.map((objective, index) => (
                  <div key={objective.id} className="border rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow">
                    {/* Header */}
                    <div className="flex items-center justify-between p-3 bg-slate-50 border-b rounded-t-lg">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-700 font-bold text-sm">
                          {index + 1}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-500">Pondération:</span>
                          <div className="flex items-center gap-1 bg-white rounded-md border px-2 py-1">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              value={objective.weight}
                              onChange={(e) => updateObjective(index, 'weight', parseInt(e.target.value) || 0)}
                              className="w-14 h-6 text-sm border-0 p-0 text-center font-medium"
                            />
                            <span className="text-sm text-slate-500">%</span>
                          </div>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeObjective(index)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Supprimer
                      </Button>
                    </div>
                    
                    {/* Body */}
                    <div className="p-4 space-y-4">
                      {/* Title and Description */}
                      <div className="space-y-3">
                        <div>
                          <Label className="text-sm font-medium">Titre de l'objectif *</Label>
                          <Input
                            placeholder="Ex: Augmenter le chiffre d'affaires de 15%"
                            value={objective.title}
                            onChange={(e) => updateObjective(index, 'title', e.target.value)}
                            className="mt-1 font-medium"
                          />
                        </div>
                        
                        <div>
                          <Label className="text-sm font-medium">Description</Label>
                          <Textarea
                            placeholder="Décrivez l'objectif en détail..."
                            value={objective.description}
                            onChange={(e) => updateObjective(index, 'description', e.target.value)}
                            rows={2}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      
                      {/* SMART Criteria - Collapsible */}
                      <details className="group">
                        <summary className="cursor-pointer flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900">
                          <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" />
                          Critères SMART (optionnel)
                        </summary>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pl-6">
                          <div className="space-y-1">
                            <Label className="text-xs text-blue-600 font-medium">S - Spécifique</Label>
                            <Input
                              placeholder="Quoi exactement ?"
                              value={objective.specific}
                              onChange={(e) => updateObjective(index, 'specific', e.target.value)}
                              className="text-sm h-9"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-green-600 font-medium">M - Mesurable</Label>
                            <Input
                              placeholder="Comment mesurer ?"
                              value={objective.measurable}
                              onChange={(e) => updateObjective(index, 'measurable', e.target.value)}
                              className="text-sm h-9"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-purple-600 font-medium">A - Atteignable</Label>
                            <Input
                              placeholder="Ressources nécessaires ?"
                              value={objective.achievable}
                              onChange={(e) => updateObjective(index, 'achievable', e.target.value)}
                              className="text-sm h-9"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-orange-600 font-medium">R - Réaliste</Label>
                            <Input
                              placeholder="Pourquoi pertinent ?"
                              value={objective.relevant}
                              onChange={(e) => updateObjective(index, 'relevant', e.target.value)}
                              className="text-sm h-9"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-red-600 font-medium">T - Temporel</Label>
                            <Input
                              placeholder="Échéance ?"
                              value={objective.timeBound}
                              onChange={(e) => updateObjective(index, 'timeBound', e.target.value)}
                              className="text-sm h-9"
                            />
                          </div>
                        </div>
                      </details>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* TAB: Compétences */}
          <TabsContent value="skills" className="space-y-4 mt-4">
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Label className="text-base font-semibold">Compétences requises</Label>
                  <p className="text-sm text-slate-500">Sélectionnez les compétences et définissez le niveau requis.</p>
                </div>
                {allSkills.length === 0 && (
                  <Button variant="outline" size="sm" onClick={handleImportDefaults}>
                    <Download className="h-4 w-4 mr-2" />
                    Importer compétences par défaut
                  </Button>
                )}
              </div>
              
              {loadingSkills ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : allSkills.length === 0 ? (
                <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
                  <Award className="h-12 w-12 mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-500 mb-3">Aucune compétence disponible dans la bibliothèque.</p>
                  <div className="flex justify-center gap-2">
                    <Button variant="outline" onClick={handleImportDefaults}>
                      <Download className="h-4 w-4 mr-2" />
                      Importer compétences par défaut
                    </Button>
                    <Button variant="link" onClick={() => navigate('/skills')}>
                      Gérer la bibliothèque
                    </Button>
                  </div>
                </div>
              ) : groupedSkills.length === 0 ? (
                /* Skills without categories */
                <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                  <div className="p-3 bg-slate-50 font-medium text-sm flex items-center gap-2 border-l-4 border-indigo-500">
                    <Award className="h-4 w-4" />
                    Toutes les compétences
                    <Badge variant="secondary" className="text-xs">{allSkills.length}</Badge>
                  </div>
                  {allSkills.map(skill => (
                    <div key={skill.id} className="p-3 hover:bg-slate-50">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedSkills[skill.id]?.selected || false}
                          onCheckedChange={() => toggleSkillSelection(skill.id)}
                          className="mt-1"
                        />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{skill.name}</p>
                          {selectedSkills[skill.id]?.selected && (
                            <div className="mt-2 grid gap-3 md:grid-cols-2">
                              <div>
                                <Label className="text-xs">Niveau requis</Label>
                                <div className="flex items-center gap-2 mt-1">
                                  <Slider
                                    value={[selectedSkills[skill.id]?.requiredLevel || 3]}
                                    onValueChange={([v]) => updateSkillRequirement(skill.id, 'requiredLevel', v)}
                                    min={1}
                                    max={5}
                                    step={1}
                                    className="flex-1"
                                  />
                                  <Badge className={SKILL_LEVELS[selectedSkills[skill.id]?.requiredLevel || 3].color}>
                                    {SKILL_LEVELS[selectedSkills[skill.id]?.requiredLevel || 3].label}
                                  </Badge>
                                </div>
                              </div>
                              <div>
                                <Label className="text-xs">Importance</Label>
                                <Select 
                                  value={selectedSkills[skill.id]?.importance || 'medium'}
                                  onValueChange={(v) => updateSkillRequirement(skill.id, 'importance', v)}
                                >
                                  <SelectTrigger className="mt-1 h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="low">Faible</SelectItem>
                                    <SelectItem value="medium">Moyen</SelectItem>
                                    <SelectItem value="critical">Critique</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
                  {groupedSkills.map(category => (
                    <div key={category.id}>
                      <div 
                        className="p-3 bg-slate-50 font-medium text-sm flex items-center gap-2"
                        style={{ borderLeft: `4px solid ${category.color}` }}
                      >
                        {category.name}
                        <Badge variant="secondary" className="text-xs">{category.skills.length}</Badge>
                      </div>
                      {category.skills.map(skill => (
                        <div key={skill.id} className="p-3 hover:bg-slate-50">
                          <div className="flex items-start gap-3">
                            <Checkbox
                              checked={selectedSkills[skill.id]?.selected || false}
                              onCheckedChange={() => toggleSkillSelection(skill.id)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <p className="font-medium text-sm">{skill.name}</p>
                              {selectedSkills[skill.id]?.selected && (
                                <div className="mt-2 grid gap-3 md:grid-cols-2">
                                  <div>
                                    <Label className="text-xs">Niveau requis</Label>
                                    <div className="flex items-center gap-2 mt-1">
                                      <Slider
                                        value={[selectedSkills[skill.id]?.requiredLevel || 3]}
                                        onValueChange={([v]) => updateSkillRequirement(skill.id, 'requiredLevel', v)}
                                        min={1}
                                        max={5}
                                        step={1}
                                        className="flex-1"
                                      />
                                      <Badge className={SKILL_LEVELS[selectedSkills[skill.id]?.requiredLevel || 3].color}>
                                        {SKILL_LEVELS[selectedSkills[skill.id]?.requiredLevel || 3].label}
                                      </Badge>
                                    </div>
                                  </div>
                                  <div>
                                    <Label className="text-xs">Importance</Label>
                                    <Select 
                                      value={selectedSkills[skill.id]?.importance || 'medium'}
                                      onValueChange={(v) => updateSkillRequirement(skill.id, 'importance', v)}
                                    >
                                      <SelectTrigger className="mt-1 h-8">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="low">Faible</SelectItem>
                                        <SelectItem value="medium">Moyen</SelectItem>
                                        <SelectItem value="critical">Critique</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex items-center justify-between mt-2">
                <p className="text-sm text-slate-500">
                  {selectedSkillsCount} compétence(s) sélectionnée(s)
                </p>
                <Button variant="link" size="sm" onClick={() => navigate('/skills')}>
                  <Plus className="h-4 w-4 mr-1" />
                  Gérer la bibliothèque
                </Button>
              </div>
            </div>
          </TabsContent>
          
          {/* TAB: Détails */}
          <TabsContent value="details" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Missions principales</Label>
              <Textarea
                placeholder="Décrivez les missions principales du poste..."
                value={profileForm.missions}
                onChange={(e) => setProfileForm({...profileForm, missions: e.target.value})}
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Responsabilités</Label>
              <Textarea
                placeholder="Listez les responsabilités du poste..."
                value={profileForm.responsibilities}
                onChange={(e) => setProfileForm({...profileForm, responsibilities: e.target.value})}
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Pré-requis / Qualifications</Label>
              <Textarea
                placeholder="Formation, expérience, certifications requises..."
                value={profileForm.requirements}
                onChange={(e) => setProfileForm({...profileForm, requirements: e.target.value})}
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Conditions de travail</Label>
              <Textarea
                placeholder="Horaires, déplacements, équipements..."
                value={profileForm.workingConditions}
                onChange={(e) => setProfileForm({...profileForm, workingConditions: e.target.value})}
                rows={2}
              />
            </div>
          </TabsContent>

          {/* === ONGLET CARRIÈRE === */}
          <TabsContent value="career" className="space-y-4 mt-4">
            <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-3 text-xs text-indigo-700">
              Rattachez ce poste à votre référentiel de carrière (famille métier, niveau séniorité) et précisez
                  les exigences (expérience minimale, formations obligatoires, certifications recommandées). Ces
                  informations alimentent les analyses GPEC et les recommandations de mobilité.
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Famille métier</Label>
                <Select
                  value={profileForm.familyId || 'none'}
                  onValueChange={(v) => setProfileForm({ ...profileForm, familyId: v === 'none' ? '' : v })}
                >
                  <SelectTrigger data-testid="career-family-select">
                    <SelectValue placeholder="Aucune" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune</SelectItem>
                    {families.map(f => (
                      <SelectItem key={f.id} value={f.id} data-testid={`career-family-${f.id}`}>
                        {f.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {families.length === 0 && (
                  <p className="text-xs text-slate-400 mt-1">
                    Créez vos familles depuis Talents → Référentiel carrière
                  </p>
                )}
              </div>

              <div>
                <Label>Niveau de séniorité</Label>
                <Select
                  value={profileForm.seniorityLevelId || 'none'}
                  onValueChange={(v) => setProfileForm({ ...profileForm, seniorityLevelId: v === 'none' ? '' : v })}
                >
                  <SelectTrigger data-testid="career-seniority-select">
                    <SelectValue placeholder="Aucun" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {seniorityLevels.map(l => (
                      <SelectItem key={l.id} value={l.id} data-testid={`career-lvl-${l.id}`}>
                        {l.name} {l.minYearsExperience > 0 && `(≥ ${l.minYearsExperience} ans)`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2">
                <Label>Années minimum d'expérience</Label>
                <Input
                  type="number"
                  min={0}
                  max={50}
                  value={profileForm.minYearsExperience}
                  onChange={(e) => setProfileForm({ ...profileForm, minYearsExperience: e.target.value })}
                  placeholder="ex : 3"
                  data-testid="career-min-years-input"
                  className="max-w-xs"
                />
              </div>
            </div>

            {/* Formations obligatoires */}
            <div>
              <Label>Formations obligatoires</Label>
              <p className="text-xs text-slate-500 mb-2">Saisissez une formation puis appuyez sur Entrée</p>
              <div className="flex gap-2">
                <Input
                  value={trainingInput}
                  onChange={(e) => setTrainingInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const v = trainingInput.trim();
                      if (v && !profileForm.mandatoryTrainings.includes(v)) {
                        setProfileForm({
                          ...profileForm,
                          mandatoryTrainings: [...profileForm.mandatoryTrainings, v],
                        });
                      }
                      setTrainingInput('');
                    }
                  }}
                  placeholder="ex : Sécurité au travail (SST)"
                  data-testid="career-training-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const v = trainingInput.trim();
                    if (v && !profileForm.mandatoryTrainings.includes(v)) {
                      setProfileForm({
                        ...profileForm,
                        mandatoryTrainings: [...profileForm.mandatoryTrainings, v],
                      });
                    }
                    setTrainingInput('');
                  }}
                  data-testid="career-add-training-btn"
                >
                  Ajouter
                </Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2" data-testid="career-trainings-list">
                {profileForm.mandatoryTrainings.map((t, idx) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className="bg-amber-50 text-amber-700 border-amber-200 pr-1"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => setProfileForm({
                        ...profileForm,
                        mandatoryTrainings: profileForm.mandatoryTrainings.filter((_, i) => i !== idx),
                      })}
                      className="ml-1.5 hover:text-red-600"
                      aria-label="Retirer"
                    >×</button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Certifications recommandées */}
            <div>
              <Label>Certifications recommandées</Label>
              <p className="text-xs text-slate-500 mb-2">Saisissez une certification puis appuyez sur Entrée</p>
              <div className="flex gap-2">
                <Input
                  value={certInput}
                  onChange={(e) => setCertInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const v = certInput.trim();
                      if (v && !profileForm.recommendedCertifications.includes(v)) {
                        setProfileForm({
                          ...profileForm,
                          recommendedCertifications: [...profileForm.recommendedCertifications, v],
                        });
                      }
                      setCertInput('');
                    }
                  }}
                  placeholder="ex : PMP, ITIL, AWS Solutions Architect..."
                  data-testid="career-cert-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const v = certInput.trim();
                    if (v && !profileForm.recommendedCertifications.includes(v)) {
                      setProfileForm({
                        ...profileForm,
                        recommendedCertifications: [...profileForm.recommendedCertifications, v],
                      });
                    }
                    setCertInput('');
                  }}
                  data-testid="career-add-cert-btn"
                >
                  Ajouter
                </Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2" data-testid="career-certs-list">
                {profileForm.recommendedCertifications.map((t, idx) => (
                  <Badge
                    key={idx}
                    variant="outline"
                    className="bg-indigo-50 text-indigo-700 border-indigo-200 pr-1"
                  >
                    {t}
                    <button
                      type="button"
                      onClick={() => setProfileForm({
                        ...profileForm,
                        recommendedCertifications: profileForm.recommendedCertifications.filter((_, i) => i !== idx),
                      })}
                      className="ml-1.5 hover:text-red-600"
                      aria-label="Retirer"
                    >×</button>
                  </Badge>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
        
        <DialogFooter className="mt-6 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleSaveProfile}
            disabled={saving}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                {mode === 'create' ? 'Créer et attribuer' : 'Enregistrer'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
